import { PDFDocument } from "pdf-lib";

const FORM_URL = "/assets/forms/f1099nec-2025.pdf";
const FORM_1096_URL = "/assets/forms/f1096-2025.pdf";

// Cache the original IRS PDF bytes so we only fetch once per session
let _cachedBytes = null;
const getFormBytes = async () => {
  if (!_cachedBytes) {
    _cachedBytes = await fetch(FORM_URL).then((r) => {
      if (!r.ok) throw new Error(`Could not load IRS form: ${r.status}`);
      return r.arrayBuffer();
    });
  }
  return _cachedBytes;
};

/**
 * Field paths for each copy of the 1099-NEC.
 * CopyA uses f1_* prefix; CopyB/Copy1/Copy2 use f2_* prefix.
 * Derived from inspecting the AcroForm widget rects on each page.
 */
const COPIES = [
  {
    name: "CopyA",
    year:             "topmostSubform[0].CopyA[0].PgHeader[0].CalendarYear[0].f1_1[0]",
    payer:            "topmostSubform[0].CopyA[0].LeftCol[0].f1_2[0]",
    payerTIN:         "topmostSubform[0].CopyA[0].LeftCol[0].f1_3[0]",
    recipientTIN:     "topmostSubform[0].CopyA[0].LeftCol[0].f1_4[0]",
    recipientName:    "topmostSubform[0].CopyA[0].LeftCol[0].f1_5[0]",
    recipientAddress: "topmostSubform[0].CopyA[0].LeftCol[0].f1_6[0]",
    recipientCity:    "topmostSubform[0].CopyA[0].LeftCol[0].f1_7[0]",
    accountNum:       "topmostSubform[0].CopyA[0].LeftCol[0].f1_8[0]",
    box1:             "topmostSubform[0].CopyA[0].RightCol[0].f1_9[0]",
    box4:             "topmostSubform[0].CopyA[0].RightCol[0].f1_10[0]",
  },
  ...["CopyB", "Copy1", "Copy2"].map((copy) => ({
    name: copy,
    year:             `topmostSubform[0].${copy}[0].PgHeader[0].CalendarYear[0].f2_1[0]`,
    payer:            `topmostSubform[0].${copy}[0].LeftCol[0].f2_2[0]`,
    payerTIN:         `topmostSubform[0].${copy}[0].LeftCol[0].f2_3[0]`,
    recipientTIN:     `topmostSubform[0].${copy}[0].LeftCol[0].f2_4[0]`,
    recipientName:    `topmostSubform[0].${copy}[0].LeftCol[0].f2_5[0]`,
    recipientAddress: `topmostSubform[0].${copy}[0].LeftCol[0].f2_6[0]`,
    recipientCity:    `topmostSubform[0].${copy}[0].LeftCol[0].f2_7[0]`,
    accountNum:       `topmostSubform[0].${copy}[0].LeftCol[0].f2_8[0]`,
    box1:             `topmostSubform[0].${copy}[0].RightCol[0].f2_9[0]`,
    box4:             `topmostSubform[0].${copy}[0].RightCol[0].f2_10[0]`,
  })),
];

/**
 * Loads the official IRS 1099-NEC PDF, fills all four copies with
 * the provided data, and returns the filled PDF as a Uint8Array.
 */
export async function fillPDFBytes(data) {
  const {
    taxYear = "",
    payerLine1 = "",
    payerLine2 = "",
    payerLine3 = "",
    payerTIN = "",
    recipientTIN = "",
    recipientName = "",
    recipientAddress = "",
    recipientCityLine = "",
    box1 = "",
    box4 = "",
  } = data;

  const payerBlock = [payerLine1, payerLine2, payerLine3]
    .filter(Boolean)
    .join("\n");

  const formBytes = await getFormBytes();
  const pdfDoc = await PDFDocument.load(formBytes, { ignoreEncryption: true });
  const form = pdfDoc.getForm();

  const setField = (fieldName, value) => {
    try {
      form.getTextField(fieldName).setText(value || "");
    } catch {
      // Field may not exist on this copy — skip silently
    }
  };

  for (const copy of COPIES) {
    setField(copy.year,             taxYear);
    setField(copy.payer,            payerBlock);
    setField(copy.payerTIN,         payerTIN);
    setField(copy.recipientTIN,     recipientTIN);
    setField(copy.recipientName,    recipientName);
    setField(copy.recipientAddress, recipientAddress);
    setField(copy.recipientCity,    recipientCityLine);
    setField(copy.box1,             box1);
    setField(copy.box4,             box4);
  }

  return pdfDoc.save();
}

/**
 * Loads the official IRS 1096 PDF, fills it with summary data derived
 * from a single 1099-NEC, and returns the filled PDF as a Uint8Array.
 */
export async function fillForm1096Bytes(data) {
  const {
    payerLine1 = "",
    payerLine2 = "",
    payerLine3 = "",
    payerTIN = "",
    ssn = "",           // Box 2 — SSN (individual filers)
    totalForms = "1",   // Box 3 — total number of forms
    box4 = "",          // Box 4 — federal income tax withheld
    box1 = "",          // Box 5 — total amount reported
    contactName = "",
    contactPhone = "",
    contactEmail = "",
    faxNumber = "",
  } = data;

  const formBytes = await fetch(FORM_1096_URL).then((r) => {
    if (!r.ok) throw new Error(`Could not load 1096 form: ${r.status}`);
    return r.arrayBuffer();
  });

  const pdfDoc = await PDFDocument.load(formBytes, { ignoreEncryption: true });
  const form = pdfDoc.getForm();

  const setField = (fieldName, value) => {
    try { form.getTextField(fieldName).setText(value || ""); } catch {}
  };
  const checkField = (fieldName) => {
    try { form.getCheckBox(fieldName).check(); } catch {}
  };

  // Filer name / address
  setField("topmostSubform[0].Page1[0].NameAddress_ReadOrder[0].f1_1[0]", payerLine1);
  setField("topmostSubform[0].Page1[0].NameAddress_ReadOrder[0].f1_2[0]", payerLine2);
  setField("topmostSubform[0].Page1[0].NameAddress_ReadOrder[0].f1_3[0]", payerLine3);
  // Contact info row
  setField("topmostSubform[0].Page1[0].NameAddress_ReadOrder[0].f1_4[0]", contactName);
  setField("topmostSubform[0].Page1[0].NameAddress_ReadOrder[0].f1_5[0]", contactPhone);
  setField("topmostSubform[0].Page1[0].NameAddress_ReadOrder[0].f1_6[0]", contactEmail);
  setField("topmostSubform[0].Page1[0].NameAddress_ReadOrder[0].f1_7[0]", faxNumber);
  // Box row (left to right: EIN, Box 2 SSN, Box 3 # forms, Box 4 withheld, Box 5 total amount)
  setField("topmostSubform[0].Page1[0].f1_8[0]",                          payerTIN);
  setField("topmostSubform[0].Page1[0].f1_9[0]",                          ssn);
  setField("topmostSubform[0].Page1[0].f1_10[0]",                         String(totalForms));
  setField("topmostSubform[0].Page1[0].Box4_ReadOrder[0].f1_11[0]",       box4);
  setField("topmostSubform[0].Page1[0].f1_12[0]",                         box1);
  checkField("topmostSubform[0].Page1[0].F1099-NEC[0].c1_1[0]");

  return pdfDoc.save();
}

/**
 * Fills the IRS PDF and triggers a browser download.
 */
export async function download1099PDF(data) {
  const bytes = await fillPDFBytes(data);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeName = (data.recipientName || "recipient").replace(/\s+/g, "_");
  a.download = `1099-NEC-${data.taxYear || ""}__${safeName}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Given an array of form data objects (one per 1099 filing), fills each
 * 1099 PDF and extracts its Copy A pages (pages 0 and 1, the IRS copy),
 * then merges them all into a single PDF returned as a Uint8Array.
 */
export async function mergeCopyABytes(filings) {
  const { PDFDocument: PDFDoc } = await import("pdf-lib");
  const merged = await PDFDoc.create();

  for (const data of filings) {
    const filled = await fillPDFBytes(data);
    const src = await PDFDoc.load(filled, { ignoreEncryption: true });
    // Page 1 (index 1) is Copy A (IRS copy); page 0 is the instructions sheet — exclude it
    const [p1] = await merged.copyPages(src, [1]);
    merged.addPage(p1);
  }

  return merged.save();
}

export default download1099PDF;
